import { useState } from 'react';
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  onSnapshot,
  Firestore,
} from 'firebase/firestore';
import { Database, Loader2, CheckCircle2, XCircle, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firestoreForDatabase, DEFAULT_DATABASE_ID } from '../../lib/orgDb';

/**
 * Named-database feasibility spike (hub-and-spoke Phase 0 —
 * docs/plans/2026-08-09-hub-and-spoke-org-data-plane.md).
 *
 * Measures, from a REAL signed-in browser session, whether the client SDK's
 * auth integration works against a named Firestore database — the question
 * that blocked per-org databases in 2026 (CLAUDE.md). Four checks (write,
 * get, listen, delete) against `dbSpike/{uid}` — a rules path that REQUIRES
 * auth + self-ownership — run first on `(default)` (the instrument firing
 * on a known positive) and then on the named DB.
 *
 * Prereq: scripts/spike_named_db.sh has created the named DB and deployed
 * the same rules to it. All four named-DB checks PASS ⇒ per-org data
 * planes are unblocked; any FAIL ⇒ the errors shown ARE the finding.
 */

interface CheckResult {
  name: string;
  ok: boolean;
  detail: string;
  ms: number;
}

interface SuiteResult {
  databaseId: string;
  startedAt: string;
  checks: CheckResult[];
}

async function runChecks(dbInstance: Firestore, uid: string): Promise<CheckResult[]> {
  const results: CheckResult[] = [];
  const probeRef = doc(dbInstance, 'dbSpike', uid);
  const payload = { probe: true, at: Date.now(), by: uid };

  const timed = async (name: string, fn: () => Promise<string>) => {
    const t0 = performance.now();
    try {
      const detail = await fn();
      results.push({ name, ok: true, detail, ms: Math.round(performance.now() - t0) });
    } catch (err) {
      results.push({
        name,
        ok: false,
        detail: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
        ms: Math.round(performance.now() - t0),
      });
    }
  };

  await timed('write (setDoc under auth rules)', async () => {
    await setDoc(probeRef, payload);
    return 'doc written';
  });
  await timed('get (readback)', async () => {
    const snap = await getDoc(probeRef);
    if (!snap.exists()) throw new Error('doc missing on readback');
    if (snap.data()?.at !== payload.at) throw new Error('readback content mismatch');
    return 'readback matches written content';
  });
  await timed('listen (onSnapshot delivers)', async () => {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        unsub();
        reject(new Error('no snapshot within 10s'));
      }, 10_000);
      const unsub = onSnapshot(
        probeRef,
        (snap) => {
          clearTimeout(timer);
          unsub();
          snap.exists()
            ? resolve('snapshot delivered with data')
            : reject(new Error('snapshot delivered but doc missing'));
        },
        (err) => {
          clearTimeout(timer);
          unsub();
          reject(err);
        },
      );
    });
  });
  await timed('delete (cleanup allowed)', async () => {
    await deleteDoc(probeRef);
    const snap = await getDoc(probeRef);
    if (snap.exists()) throw new Error('doc still present after delete');
    return 'deleted and confirmed gone';
  });

  return results;
}

function CheckRow({ c }: { c: CheckResult }) {
  return (
    <div className="flex items-start gap-2 text-sm py-1">
      {c.ok ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
      )}
      <span className="font-medium text-gray-800">{c.name}</span>
      <span className={`text-xs mt-0.5 ${c.ok ? 'text-gray-500' : 'text-red-700'}`}>
        {c.detail} ({c.ms}ms)
      </span>
    </div>
  );
}

export default function DbSpike() {
  const { user } = useAuth();
  const [dbId, setDbId] = useState('org-spike');
  const [running, setRunning] = useState(false);
  const [suites, setSuites] = useState<SuiteResult[]>([]);

  const run = async () => {
    if (!user) return;
    setRunning(true);
    setSuites([]);
    const out: SuiteResult[] = [];
    // Instrument proof first: the same checks must PASS on (default),
    // where client auth is known-good. Only then is a named-DB failure
    // attributable to the named DB rather than to this harness.
    for (const target of [DEFAULT_DATABASE_ID, dbId.trim()]) {
      const checks = await runChecks(firestoreForDatabase(target), user.uid);
      out.push({ databaseId: target, startedAt: new Date().toISOString(), checks });
      setSuites([...out]);
      if (target === DEFAULT_DATABASE_ID && checks.some((c) => !c.ok)) {
        // Broken instrument — a named-DB result would be meaningless.
        break;
      }
    }
    setRunning(false);
  };

  const defaultSuite = suites.find((s) => s.databaseId === DEFAULT_DATABASE_ID);
  const namedSuite = suites.find((s) => s.databaseId !== DEFAULT_DATABASE_ID);
  const verdict =
    namedSuite &&
    (namedSuite.checks.every((c) => c.ok)
      ? 'PASS — client-SDK auth works against this named database; per-org data planes are unblocked.'
      : 'FAIL — the errors above are the finding; per-org DBs stay blocked (deliver dedicated project instead).');

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">Named-database spike</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Measures whether client-SDK auth works against a named Firestore database
        (hub-and-spoke Phase 0). Run <code>scripts/spike_named_db.sh</code> first to
        create the database and deploy rules to it.
      </p>

      <div className="flex gap-2 items-center mb-6">
        <input
          value={dbId}
          onChange={(e) => setDbId(e.target.value)}
          className="rounded-lg border-gray-300 text-sm font-mono focus:ring-blue-500 focus:border-blue-500"
          placeholder="org-spike"
        />
        <button
          onClick={run}
          disabled={running || !dbId.trim()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium text-white transition-colors"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run measurement
        </button>
      </div>

      {defaultSuite && (
        <div className="rounded-xl bg-white ring-1 ring-gray-200 shadow-sm p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            (default) — instrument proof
          </h3>
          {defaultSuite.checks.map((c) => (
            <CheckRow key={c.name} c={c} />
          ))}
          {defaultSuite.checks.some((c) => !c.ok) && (
            <p className="text-xs text-red-700 mt-2">
              The harness itself failed on (default) — fix this before reading anything
              into a named-DB result.
            </p>
          )}
        </div>
      )}

      {namedSuite && (
        <div className="rounded-xl bg-white ring-1 ring-gray-200 shadow-sm p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            {namedSuite.databaseId} — the measurement
          </h3>
          {namedSuite.checks.map((c) => (
            <CheckRow key={c.name} c={c} />
          ))}
        </div>
      )}

      {verdict && (
        <div
          className={`rounded-lg p-3 text-sm font-medium ${
            verdict.startsWith('PASS')
              ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
              : 'bg-red-50 text-red-900 ring-1 ring-red-200'
          }`}
        >
          {verdict}
        </div>
      )}

      {suites.length > 0 && !running && (
        <details className="mt-4">
          <summary className="text-xs text-gray-500 cursor-pointer">
            Evidence JSON (paste into the design doc)
          </summary>
          <pre className="mt-2 text-xs bg-gray-50 ring-1 ring-gray-200 rounded p-3 overflow-x-auto select-all">
            {JSON.stringify(suites, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
